<template>
  <div class="tw-flex">
    <div
      v-if="showTitle"
      class="tw-flex tw-items-center tw-text-dt-body tw-text-dark-gray tw-mr-3"
    >
      <template v-if="shareLabel">
        {{ shareLabel }}
      </template>
      <template v-else-if="title">
        {{ t('global.shareThis', { title }) }}
      </template>
      <template v-else>
        {{ t('global.share') }}
      </template>
    </div>
    <QBtn
      :round="!flat"
      :flat="flat"
      :color="color"
      :rounded="rounded"
      :class="shareButtonClass"
    >
      <QIcon name="sym_o_share" :size="iconSize || '24px'" />
      <QMenu
        anchor="bottom right"
        self="top right"
        :class="menuClass"
      >
        <div :class="menuContainerClass">
          <QBtn
            v-for="network in networks"
            :key="network"
            :color="menuItemColor"
            :flat="menuItemFlat"
            :dense="menuItemDense"
            :no-caps="menuItemNoCaps"
            :icon="network === 'Facebook' ? 'fab fa-facebook' : 'fab fa-line'"
            :class="menuItemClass"
            :label="showMenuItemLabels ? network : undefined"
            @click="shareContent(network)"
          />
          <QBtn
            :flat="menuItemFlat"
            :color="menuItemColor"
            :dense="menuItemDense"
            :no-caps="menuItemNoCaps"
            icon="content_copy"
            :class="menuItemClass"
            :label="showMenuItemLabels ? t('global.copyUrl') : undefined"
            @click="copyUrl"
          />
        </div>
      </QMenu>
    </QBtn>
  </div>
</template>

<script setup lang="ts">
const props = withDefaults(defineProps<{
  title?: string
  categoryEventName?: string
  titleName?: string
  eventName?: string
  flat?: boolean
  showTitle?: boolean
  color?: string
  rounded?: boolean
  // Menu props
  menuClass?: string
  menuContainerClass?: string
  menuItemClass?: string
  menuItemFlat?: boolean
  menuItemDense?: boolean
  menuItemNoCaps?: boolean
  menuItemColor?: string
  showMenuItemLabels?: boolean
  btnClass?: string
  iconSize?: string
  shareLabel?: string
  themePrimaryCircle?: boolean
}>(), {
  title: '',
  categoryEventName: '',
  titleName: '',
  eventName: '',
  flat: false,
  showTitle: true,
  color: 'primary',
  rounded: false,
  // Menu props defaults
  menuClass: 'tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-2',
  menuContainerClass: 'tw-flex tw-flex-col',
  menuItemClass: 'tw-w-full tw-justify-start tw-px-4 tw-py-2 hover:tw-bg-gray-100',
  menuItemFlat: true,
  menuItemDense: false,
  menuItemNoCaps: false,
  menuItemColor: 'grey',
  showMenuItemLabels: true,
  btnClass: '',
  iconSize: '',
  shareLabel: '',
  themePrimaryCircle: false,
})

const shareButtonClass = computed(() => [
  props.btnClass,
  { 'theme-primary-circle-share-btn': props.themePrimaryCircle },
])

const networks = ref<string[]>(['Facebook', 'Line'])
const url = ref<string>('')

onMounted(() => {
  url.value = window.location.href
})

const shareContent = (network: string) => {
  const shareUrl = new URL('https://www.facebook.com/sharer/sharer.php')

  if (network === 'Facebook') {
    shareUrl.searchParams.set('u', url.value)
    window.open(shareUrl.toString(), '_blank', 'width=600,height=400')
  } else if (network === 'Line') {
    window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url.value)}`, '_blank', 'width=600,height=400')
  }

  trackSharingEvent(network)
}

const notify = useNotify()
const { t } = useI18n()
const copyUrl = () => {
  navigator.clipboard.writeText(url.value)
    .then(() => {
      notify.success(t('global.urlCopied'))
    })
    .catch((err) => {
      console.error('Failed to copy URL: ', err)
      notify.error(t('global.copyFailed'))
    })
  trackSharingEvent('Copy URL')
}

const { analyticEvent, fbPixelEvent } = useAnalytic()
const trackSharingEvent = (network: string) => {
  analyticEvent({
    ga4ActionName: props.eventName,
    ga4Label: `${network} - ${props.titleName}`,
  })

  fbPixelEvent({
    fbCategory: 'trackCustom',
    fbEvent: 'Social Shared',
    fbValue: { label: `${network} - ${props.titleName}` },
  })
}
</script>

<style>
.q-menu .q-btn__content {
  justify-content: flex-start !important;
}

.q-menu .q-icon.on-left {
  width: 24px;
  text-align: center;
}

.theme-primary-circle-share-btn.q-btn {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  min-height: 32px !important;
  padding: 0 !important;
  border-radius: 9999px !important;
  background: var(--theme-color-primary) !important;
  color: var(--theme-color-inverted-primary) !important;
}

.theme-primary-circle-share-btn.q-btn::before {
  border-radius: 9999px !important;
}

.theme-primary-circle-share-btn.q-btn .q-btn__content {
  width: 100%;
  height: 100%;
}
</style>
